import React, {
	useMemo,
	useCallback,
	type ReactNode,
	type MouseEvent,
	type RefObject,
} from 'react';
import noop from 'lodash/noop';
import { Box } from '@atlaskit/primitives';
import type { ConfigureSource } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import type { UFOExperience } from '@atlassian/ufo';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
import { asyncEditIssueTypeModalEntrypoint } from '@atlassian/jira-edit-issue-type-modal/entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type EditIssueTypeModalEntrypointTriggerProps = {
	children: ReactNode;
	queryParams: {
		issueTypeId: string;
		projectId: number;
		projectKey: string;
		ufoExperience: UFOExperience;
		calledFrom: ConfigureSource;
		onRefetchIssueTypesWhenIssueTypeUpdated: () => void;
		onEditModalClose: () => void;
		// Remove this prop when cleaning up inline_config_a11y_improvements
		buttonRef?: RefObject<HTMLElement> | null;
	};
	// Remove this prop when cleaning up inline_config_a11y_improvements
	onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export const EditIssueTypeModalEntrypointTrigger = ({
	children,
	queryParams,
	onClick = noop,
}: EditIssueTypeModalEntrypointTriggerProps) => {
	const { projectKey, onEditModalClose, buttonRef, ...props } = queryParams;

	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const entryPointParams = useMemo(
		() => ({
			projectKey,
			cloudId,
		}),
		[cloudId, projectKey],
	);

	const onCloseEditIssueTypeModal = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'inlineEditIssueTypeModal',
				action: 'closed',
			}),
			{
				calledFrom: props.calledFrom,
			},
		);
		onEditModalClose();
	}, [createAnalyticsEvent, onEditModalClose, props.calledFrom]);

	const entryPointProps = useMemo(
		() => ({
			...props,
			onClose: onCloseEditIssueTypeModal,
		}),
		[props, onCloseEditIssueTypeModal],
	);

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncEditIssueTypeModalEntrypoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			interactionName="edit-issue-type-modal"
			useInternalModal
			modalProps={defaultModalProps}
			forwardedRef={!fg('inline_config_a11y_improvements') ? buttonRef : undefined}
		>
			{({ ref }) => (
				<Box ref={ref} onClick={!fg('inline_config_a11y_improvements') ? onClick : undefined}>
					{children}
				</Box>
			)}
		</ModalEntryPointPressableTrigger>
	);
};
